import { CheckCircle, XCircle, ExternalLink, Lightbulb } from "lucide-react";

export const DomainWarmupBestPractices = () => {
  const dos = [
    {
      title: "Set up authentication first",
      description: "Configure SPF, DKIM, and DMARC before sending any emails. This is non-negotiable for building reputation.",
      source: "Google Postmaster"
    },
    {
      title: "Start with engaged recipients",
      description: "Send initial emails to contacts who have recently engaged with your brand—they're most likely to open and interact.",
      source: "Validity 2025"
    },
    {
      title: "Monitor Google Postmaster Tools daily",
      description: "Track domain reputation, spam rate, and authentication status. Respond to issues within 24 hours.",
      source: "Google Postmaster"
    },
    {
      title: "Use a dedicated IP for high volume",
      description: "If sending 100,000+ emails/month, a dedicated IP lets you control your reputation independently.",
      source: "SendGrid 2024"
    },
    {
      title: "Maintain consistent sending patterns",
      description: "Send similar volumes at similar times each day. Predictability builds trust with ISPs.",
      source: "Mailgun 2024"
    },
    {
      title: "Clean your list before warmup",
      description: "Remove invalid, bounced, and inactive emails. A clean list prevents reputation damage from the start.",
      source: "ZeroBounce 2024"
    }
  ];

  const donts = [
    {
      title: "Don't skip the warmup period",
      description: "Sending high volumes immediately signals spam behavior. ISPs will throttle or block your domain.",
      impact: "Can result in domain blacklisting"
    },
    {
      title: "Don't send to purchased lists",
      description: "Purchased lists have high bounce rates and spam complaints that will destroy your sender reputation.",
      impact: "Near-certain spam folder placement"
    },
    {
      title: "Don't ignore bounce rates",
      description: "Hard bounces above 2% signal poor list quality. ISPs use this as a spam indicator.",
      impact: "Triggers ISP throttling"
    },
    {
      title: "Don't use misleading subject lines",
      description: "Clickbait subjects lead to spam complaints when content doesn't match. This damages reputation permanently.",
      impact: "Increases spam complaints by 3-5x"
    },
    {
      title: "Don't send during off-hours only",
      description: "Sending exclusively at 3 AM or on weekends looks like bot behavior to spam filters.",
      impact: "Triggers automated pattern detection"
    },
    {
      title: "Don't change domains to escape reputation",
      description: "ISPs track domain history and connected infrastructure. New domains with bad patterns get flagged faster.",
      impact: "Accelerated blacklisting"
    }
  ];

  const sources = [
    {
      name: "Google Postmaster Tools",
      url: "https://postmaster.google.com/",
      description: "Monitor Gmail-specific deliverability metrics"
    },
    {
      name: "Validity Sender Score",
      url: "https://senderscore.org/",
      description: "Check your IP and domain reputation score"
    },
    {
      name: "MXToolbox",
      url: "https://mxtoolbox.com/blacklists.aspx",
      description: "Check if your domain is on blacklists"
    },
    {
      name: "Mail-Tester",
      url: "https://www.mail-tester.com/",
      description: "Test your email deliverability score"
    },
    {
      name: "Lemlist Warmup Guide",
      url: "https://www.lemlist.com/blog/email-warmup",
      description: "Comprehensive cold email warmup strategies"
    },
    {
      name: "SendGrid Warmup Documentation",
      url: "https://docs.sendgrid.com/ui/sending-email/warming-up-an-ip-address",
      description: "Official IP warmup best practices"
    }
  ];

  return (
    <section className="py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-5 h-5 text-primary" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Best Practices & Common Mistakes
          </h2>
        </div>
        <p className="text-muted-foreground">
          Industry-proven strategies to maximize warmup success
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Do's */}
        <div className="bg-success/5 border border-success/20 rounded-xl p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-success" />
            <h3 className="font-display text-lg font-semibold text-foreground">Do's</h3>
          </div>
          <div className="space-y-4">
            {dos.map((item, index) => (
              <div key={index} className="border-b border-success/10 last:border-0 pb-4 last:pb-0">
                <h4 className="font-semibold text-foreground text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground mb-1">{item.description}</p>
                <span className="text-[10px] text-success/70">Source: {item.source}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Don'ts */}
        <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <XCircle className="w-5 h-5 text-destructive" />
            <h3 className="font-display text-lg font-semibold text-foreground">Don'ts</h3>
          </div>
          <div className="space-y-4">
            {donts.map((item, index) => (
              <div key={index} className="border-b border-destructive/10 last:border-0 pb-4 last:pb-0">
                <h4 className="font-semibold text-foreground text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground mb-1">{item.description}</p>
                <span className="text-[10px] text-destructive/70">Impact: {item.impact}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sources */}
      <div className="bg-muted/30 border border-border rounded-xl p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <ExternalLink className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-semibold text-foreground">
            Useful Tools & Resources
          </h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sources.map((source, index) => (
            <a
              key={index}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-background border border-border rounded-lg p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                  {source.name}
                </span>
                <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <p className="text-xs text-muted-foreground">{source.description}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
